// filepath: src/components/products/ProductTable.js
import React from "react";
import { Package } from "lucide-react";
import TableActionButtons from '../../CommonComponents/TableActionButtons';
import EmptyState from '../../CommonComponents/EmptyState';
import ProductImageFallback from '../../CommonComponents/ProductImageFallback';
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from '../../../utils/tableStyle';

// Utility function to format price in PHP currency
const formatPrice = (amount) => {
    if (isNaN(amount)) return "₱0.00";
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(amount);
};

// Helper component for product image with placeholder fallback and out-of-stock overlay
const ProductImage = ({ imageUrl, name, uploadsBaseURL, outOfStock }) => {
    const [src, setSrc] = React.useState(imageUrl ? `${uploadsBaseURL}/${imageUrl}` : null);

    return (
        <div className="relative h-10 w-10">
            {src ? (
                <img
                    src={src}
                    alt={name}
                    className="h-10 w-10 object-cover rounded border"
                    onError={() => setSrc(null)}
                />
            ) : (
                <ProductImageFallback className="h-10 w-10 rounded" aria-label="No product image" />
            )}
            {outOfStock && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded z-10">
                    <span className="text-white text-[10px] font-bold">No stock</span>
                </div>
            )}
        </div>
    );
};

// ProductTable component for admin product management
export default function ProductTable({ products = [], onEdit, onDelete, uploadsBaseURL, showActions = true, showBranchPrice = false }) {
    const colCount = showActions ? (showBranchPrice ? 10 : 9) : (showBranchPrice ? 9 : 8);
    return (
        <div className={tableContainer}>
            <table className={tableBase}>
                <thead className={tableHead} style={{ background: 'var(--color-primary)' }}>
                    <tr>
                        <th className={tableHeader + ' text-center'}>Image</th>
                        <th className={tableHeader + ' text-center'}>Code</th>
                        <th className={tableHeader + ' text-center'}>Name</th>
                        <th className={tableHeader + ' text-center'}>Category</th>
                        <th className={tableHeader + ' text-center'}>Universal Price</th>
                        {showBranchPrice && (
                            <th className={tableHeader + ' text-center'}>Branch Price</th>
                        )}
                        <th className={tableHeader + ' text-center'}>Unit</th>
                        <th className={tableHeader + ' text-center'}>Created</th>
                        <th className={tableHeader + ' text-center'}>Updated</th>
                        {showActions && (
                            <th className={tableHeader + ' text-center'}>Actions</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {products.length === 0 ? (
                        <tr>
                            <td colSpan={colCount} className={tableCell + ' text-center py-8'}>
                                <div className="flex flex-col items-center justify-center">
                                    <EmptyState
                                        icon={Package}
                                        message="No products found."
                                        size="default"
                                    />
                                </div>
                            </td>
                        </tr>
                    ) : (
                        products.map((product) => (
                            <tr key={product.id} className={tableRow + ' border-b last:border-0'}>
                                <td className={tableCell + ' text-center'}>
                                    <ProductImage
                                        imageUrl={product.image_url}
                                        name={product.name}
                                        uploadsBaseURL={uploadsBaseURL}
                                        outOfStock={product.quantity === 0 || product.stock === 0}
                                    />
                                </td>
                                <td className={tableCell + ' text-center'}>{product.product_code}</td>
                                <td className={tableCell + ' text-center'}>{product.name}</td>
                                <td className={tableCell + ' text-center'}>{product.category || "-"}</td>
                                <td className={tableCell + ' text-center'}>{formatPrice(product.price)}</td>
                                {showBranchPrice && (
                                    <td className={tableCell + ' text-center'}>
                                        {formatPrice(
                                            product.branch_price
                                            ?? product.branchPrices?.[0]?.price
                                            ?? product.effective_price
                                            ?? product.price
                                        )}
                                    </td>
                                )}
                                <td className={tableCell + ' text-center'}>{product.unit_type}</td>
                                <td className={tableCell + ' text-center text-xs'}>
                                    {new Date(product.createdAt).toLocaleDateString("en-PH", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })}
                                </td>
                                <td className={tableCell + ' text-center text-xs'}>
                                    {new Date(product.updatedAt).toLocaleDateString("en-PH", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                    })}
                                </td>
                                {showActions && (
                                    <td className={tableCell + ' text-center'}>
                                        <div className="flex items-center justify-center gap-2">
                                            <TableActionButtons
                                                canEdit={true}
                                                canDelete={true}
                                                canToggle={false}
                                                onEdit={() => onEdit(product)}
                                                onDelete={() => onDelete(product)}
                                            />
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
